import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Download, X } from 'lucide-react'
import { fetchM365Devices } from '@/api/charts'
import { SimplePieChart } from '@/components/charts/SimplePieChart'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { PostureLink, TrafficLightDot } from '@/components/PostureIndicator'
import { Skeleton } from '@/components/ui/skeleton'
import { exportM365DrillDownCsv } from '@/lib/exportDashboardDrillDown'
import { TRAFFIC } from '@/lib/trafficLight'

const STATUS_LABELS = {
  green: 'OK',
  amber: 'Review',
  red: 'Action needed',
  grey: 'No data',
}

const STATUS_COLOURS = {
  green: '#10b981',
  amber: '#f59e0b',
  red: '#ef4444',
  grey: '#94a3b8',
}

const AREA_LABELS = {
  onedrive: 'OneDrive',
  teams: 'Teams',
  sharepoint: 'SharePoint',
}

const LABEL_TO_STATUS = Object.fromEntries(
  Object.entries(STATUS_LABELS).map(([status, label]) => [label, status]),
)

function overallFromCounts(counts) {
  if (!counts) return 'grey'
  if (counts.red > 0) return 'red'
  if (counts.amber > 0) return 'amber'
  if (counts.green > 0) return 'green'
  return 'grey'
}

function headlineForArea(area, counts, reporting) {
  const title = AREA_LABELS[area] || area
  if (!reporting) return `No ${title} data yet`
  if (counts?.red > 0) {
    return `${counts.red} device${counts.red === 1 ? '' : 's'} need ${title} action`
  }
  if (counts?.amber > 0) {
    return `${counts.amber} device${counts.amber === 1 ? '' : 's'} need ${title} review`
  }
  if (counts?.green > 0) {
    return `All ${reporting} reporting device${reporting === 1 ? '' : 's'} look good for ${title}`
  }
  return `No ${title} data yet`
}

function countsToPieData(counts) {
  return ['green', 'amber', 'red', 'grey']
    .map((status) => ({
      label: STATUS_LABELS[status],
      value: Number(counts?.[status] || 0),
      status,
    }))
    .filter((row) => row.value > 0)
}

function M365PieCard({ area, data, selectedFilter, onSelectFilter }) {
  const counts = data?.counts
  const reporting = data?.reporting ?? 0
  const overall = overallFromCounts(counts)
  const traffic = TRAFFIC[overall] ?? TRAFFIC.grey
  const title = data?.title || AREA_LABELS[area] || area
  const headline = headlineForArea(area, counts, reporting)
  const pieData = useMemo(() => countsToPieData(counts), [counts])

  const activeLabel =
    selectedFilter?.area === area ? STATUS_LABELS[selectedFilter.status] : null

  function handleSliceClick(label) {
    const status = LABEL_TO_STATUS[label]
    if (!status) return
    onSelectFilter?.({ area, status })
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-3">
          <TrafficLightDot status={overall} className="h-4 w-4" />
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base">{title}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">{headline}</p>
          </div>
          {overall !== 'grey' && (
            <Badge variant={traffic.badge} className="shrink-0">
              {traffic.label}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <SimplePieChart
          data={pieData}
          emptyMessage={`No ${title} data yet`}
          activeLabel={activeLabel}
          onItemClick={handleSliceClick}
          colourFor={(entry) => STATUS_COLOURS[entry.status] || STATUS_COLOURS.grey}
          heightClassName="h-[180px]"
        />
        <p className="text-xs text-muted-foreground">
          {reporting} device{reporting === 1 ? '' : 's'} reporting · click a slice or legend to list
          devices
        </p>
      </CardContent>
    </Card>
  )
}

export function Microsoft365PostureSummary({ data, loading, selectedFilter, onSelectFilter }) {
  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-6 w-48" />
        <div className="grid gap-4 lg:grid-cols-3">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    )
  }

  if (!data) return null

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-base font-semibold tracking-tight">Microsoft 365</h2>
        <p className="text-sm text-muted-foreground">
          OneDrive, Teams, and SharePoint sync health for devices that have reported. Click a slice
          to list devices.
          {data.devices_with_any_m365_data != null
            ? ` ${data.devices_with_any_m365_data} device${data.devices_with_any_m365_data === 1 ? '' : 's'} with M365 data.`
            : null}
        </p>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <M365PieCard
          area="onedrive"
          data={data.onedrive}
          selectedFilter={selectedFilter}
          onSelectFilter={onSelectFilter}
        />
        <M365PieCard
          area="teams"
          data={data.teams}
          selectedFilter={selectedFilter}
          onSelectFilter={onSelectFilter}
        />
        <M365PieCard
          area="sharepoint"
          data={data.sharepoint}
          selectedFilter={selectedFilter}
          onSelectFilter={onSelectFilter}
        />
      </div>
    </div>
  )
}

function M365DeviceTable({ devices, showCustomer = false }) {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
            <th className="px-4 py-3 font-medium">Device</th>
            {showCustomer && <th className="px-4 py-3 font-medium">Customer</th>}
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium">Detail</th>
          </tr>
        </thead>
        <tbody>
          {devices.map((device) => (
            <tr key={device.agent_id} className="border-b border-border/60 last:border-0 align-top">
              <td className="px-4 py-3">
                <Link
                  to={`/agents/${device.agent_id}?tab=microsoft365`}
                  className="font-medium text-primary hover:underline"
                >
                  {device.hostname || device.display_name || `Device ${device.agent_id}`}
                </Link>
                {device.display_name && device.hostname && (
                  <p className="text-xs text-muted-foreground">{device.display_name}</p>
                )}
              </td>
              {showCustomer && (
                <td className="px-4 py-3 text-muted-foreground">
                  {device.customer_name || '—'}
                </td>
              )}
              <td className="px-4 py-3 whitespace-nowrap">
                <PostureLink
                  agentId={device.agent_id}
                  status={device.status}
                  tab="microsoft365"
                  label={AREA_LABELS[device.area] || 'Microsoft 365'}
                />
              </td>
              <td className="px-4 py-3 text-muted-foreground">
                {device.interpretation || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function Microsoft365DrillDown({
  filter,
  customerIds,
  showCustomer = false,
  onClose,
}) {
  const [page, setPage] = useState(1)
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState('')

  useEffect(() => {
    setPage(1)
    setExportError('')
  }, [filter, customerIds])

  useEffect(() => {
    if (!filter?.area || !filter?.status) {
      setResult(null)
      return
    }
    let cancelled = false
    setLoading(true)
    fetchM365Devices(customerIds, filter.area, filter.status, page)
      .then((data) => {
        if (!cancelled) setResult(data)
      })
      .catch(() => {
        if (!cancelled) setResult(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [filter, customerIds, page])

  if (!filter?.area || !filter?.status) return null

  const areaLabel = AREA_LABELS[filter.area] || filter.area
  const statusLabel = STATUS_LABELS[filter.status] || filter.status

  async function handleExport() {
    setExporting(true)
    setExportError('')
    try {
      await exportM365DrillDownCsv({
        customerIds,
        area: filter.area,
        status: filter.status,
        showCustomer,
      })
    } catch {
      setExportError('Could not export this list.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">
            {areaLabel}: {statusLabel}
          </CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            {loading
              ? 'Loading devices…'
              : result
                ? `${result.total} device${result.total === 1 ? '' : 's'}`
                : 'Could not load devices.'}
          </p>
          {exportError ? (
            <p className="mt-1 text-xs text-destructive">{exportError}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting || loading || !result?.total}
          >
            <Download className="h-4 w-4" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
          <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !result?.items?.length ? (
          <p className="text-sm text-muted-foreground">No devices in this bucket.</p>
        ) : (
          <>
            <M365DeviceTable devices={result.items} showCustomer={showCustomer} />
            {result.pages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Page {result.page} of {result.pages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= result.pages || loading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
